import { WebGlRenderer, Vec3, Rectangle } from 'lbs-pi-webgl-renderer-dev';
import { TileStage } from './webgl-support/TileStage';
import { RequestQueue } from './requests-queue/RequestQueue';
import { throttle } from 'lodash';
import { getHiDpiRatio } from '../../../render-settings';
import { IMAGE_ENHANCEMENTS } from '../../../extensions/image-adjust/consts';

// This specifies the number of pixels a chunk can go out of view before being disposed.
// This both helps the rotations from being blurred on the outside edges of the screen.
// As well as the user scrolling around as if they go slightly too far they dont need to reload the chunk.
// Approx 2 tiles
const TILE_HOLD_BORDER = 512;

/**
 * Handles rendering the viewport's tiles.
 * @param viewport
 * @constructor
 */
export function WebGLLayer (viewport) {
  // Store off vars so we don't have to constantly look them up
  const viewportDiv = viewport.vpDivGet()[0];
  const state = viewport.getVPView();
  const requestQueue = new RequestQueue(viewport.urlBuilder);
  requestQueue.monitorQueue();
  // ** DO NOT REMOVE Signal when all tiles are loaded (for capturing performance metrics) DO NOT REMOVE **
  requestQueue.events.on('all-tiles-loaded', () => {
    viewport.events.trigger('all-tiles-loaded');
    document.dispatchEvent(new Event('lbspi:wv:all-tiles-loaded'));
  });

  // Create and size a canvas
  const canvas = document.createElement('canvas');
  canvas.className = 'us-vp-canvas';
  canvas.style = 'position:absolute;width:100%;height:100%';
  viewportDiv.parentElement.insertBefore(canvas, viewportDiv);

  // Create our renderer and bind it to our canvas
  const renderer = WebGlRenderer.create(canvas);
  const screenRect = new Rectangle(0, 0, state.screenWidth, state.screenHeight);
  const webglStage = new TileStage(viewport, requestQueue, screenRect);
  renderer.loadStage(webglStage);
  renderer.resized();

  // Re-painting is necessary after textures load to ensure textures are sized and positioned correctly. Throttling
  // is necessary for performance.
  const repaint = throttle(paint, 100, { leading: false, trailing: true });
  requestQueue.events.on('tile-loaded', repaint);

  // For some reason, tiles don't reliably paint themselves when the window/tab doesn't have focus. Call paint when we gain focus.
  window.addEventListener('focus', paint);
  viewport.events.on('$destroy', () => { // and clean up too
    window.removeEventListener('focus', paint);
    renderer.unload();
    requestQueue.unload();
  });

  function paint () {
    const newZoom = state.zoom / getHiDpiRatio();
    const camX = Math.round(state.left / newZoom);
    const camY = Math.round(state.top / newZoom);
    // handle viewport resizing + border
    screenRect.left = camX - (TILE_HOLD_BORDER * getHiDpiRatio());
    screenRect.top = camY - (TILE_HOLD_BORDER * getHiDpiRatio());

    renderer.camera.move(camX, camY);

    renderer.camera.transform.rotation = new Vec3(0, 0, state.angleRad);

    renderer.update();
  }

  function resize () {
    screenRect.resize((state.screenWidth * getHiDpiRatio()) + ((TILE_HOLD_BORDER * getHiDpiRatio()) * 2), ((state.screenHeight * getHiDpiRatio()) + ((TILE_HOLD_BORDER * getHiDpiRatio()) * 2))); // Adding some border to the other side (and also making up for the - TILE_HOLD_BORDER we did on the Left/Top)
    renderer.updateViewport(state.screenWidth, state.screenHeight);
    webglStage.resize(state.screenWidth, state.screenHeight);
    renderer.resized();
  }

  function takeSnapshot () {
    return new Promise((resolve) => {
      paint();
      canvas.toBlob(resolve, 'image/jpeg', 1);
    });
  }

  function adjustImage ({ brightness, contrast, gamma }) {
    // Ranges:
    //    Inputs from viewport state are just  0 - 200.
    //    WebGL lib:
    //      Brightness: -1 - 1
    //      Contrast:    0 - 2
    //      Gamma:       100 - 0 (100 is dark, 0 is bright)
    brightness = (brightness / IMAGE_ENHANCEMENTS.brightness.default) - 1;
    contrast = contrast / IMAGE_ENHANCEMENTS.contrast.default;
    renderer.setEnhancements({ brightness, contrast, gamma });
    renderer.update();
  }

  // Expose public methods
  this.resize = resize;
  this.paint = paint;
  this.adjustImage = adjustImage;
  this.rotateChange = paint;
  this.takeSnapshot = takeSnapshot;
}
